<template>
  <div class="show_all_content_wrapper">
    <main>
      <!-- ===== Filter Form ===== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
        @click.stop
      >
        <button class="filter_toggler" @click="toggleFilterForm">
          <i class="fal fa-times"></i>
        </button>

        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Provider Name -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.provider_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.booking_number')"
                v-model.trim="filterOptions.booking_number"
              />

              <!-- Client Name -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.client_name')"
                v-model.trim="filterOptions.client_name"
              />

              <!-- Vehicle -->
              <base-select-input
                col="3"
                :optionsList="vehicles"
                :placeholder="$t('PLACEHOLDERS.vehicleNumber')"
                item-text="name"
                item-value="id"
                v-model="filterOptions.vehicle"
              />

              <!-- Booking Status -->
              <base-select-input
                col="3"
                :optionsList="booking_statuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                trackBy="value"
                label="name"
                v-model="filterOptions.status"
              />
            </div>

            <!-- Buttons -->
            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template #title>
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template #title>
                  <span>{{ $t("BUTTONS.reset_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  @click="resetFilter"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>

      <!-- ===== Table Title ===== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.bookingsManagement") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="toggleFilterForm"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <!-- ===== Data Table ===== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- No Data -->
        <template #no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <!-- Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <p>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <!-- Booking Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              v-if="$can('bookings show', 'bookings')"
              placement="bottom"
            >
              <template #title>{{ $t("BUTTONS.show") }}</template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              v-if="$can('bookings updateprice', 'bookings')"
              placement="bottom"
            >
              <template #title>{{ $t("BUTTONS.edit") }}</template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              v-if="$can('bookings delete', 'bookings')"
              placement="bottom"
            >
              <template #title>{{ $t("BUTTONS.delete") }}</template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <!-- Delete Dialog -->
        <template #top>
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.booking_number || "هذا العنصر",
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">
                  {{ $t("BUTTONS.ok") }}
                </v-btn>
                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </main>

    <!-- ===== Pagination ===== -->
    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam"
        :prev-icon="
          getAppLocale === 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
        "
        :next-icon="
          getAppLocale === 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllBookings",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    booking_statuses() {
      return [
        { id: "external", name: this.$t("STATUS.external"), value: "external" },
        { id: "not_paid", name: this.$t("STATUS.not_paid"), value: "not_paid" },
        { id: "paid", name: this.$t("STATUS.paid"), value: "paid" },
        {
          id: "confirmed",
          name: this.$t("STATUS.confirmed"),
          value: "confirmed",
        },
        { id: "pending", name: this.$t("STATUS.pending"), value: "pending" },
        {
          id: "in_progress",
          name: this.$t("STATUS.in_progress"),
          value: "in_progress",
        },
        {
          id: "cancelled",
          name: this.$t("STATUS.cancelled"),
          value: "cancelled",
        },
        { id: "finished", name: this.$t("STATUS.finished"), value: "finished" },
        {
          id: "finished_penalty",
          name: this.$t("STATUS.finished_penalty"),
          value: "finished_penalty",
        },
        { id: "deliver", name: this.$t("STATUS.deliver"), value: "deliver" },
        { id: "receive", name: this.$t("STATUS.receive"), value: "receive" },
      ];
    },
  },

  watch: {
    ["$route.query.page"](page) {
      this.paginations.current_page = +(page || 1);
      this.setTableRows();
    },
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      filterFormIsActive: false,
      filterOptions: {
        booking_number: "",
        provider_name: "",
        client_name: "",
        vehicle: null,
        status: null,
      },

      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.bookingNumber"),
          value: "booking_number",
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.client_name"),
          value: "client.name",
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "provider.name",
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          align: "center",
        },
      ],
      tableRows: [],
      vehicles: [],

      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },

      dialogDelete: false,
      itemToDelete: null,
    };
  },

  methods: {
    toggleFilterForm() {
      this.filterFormIsActive = !this.filterFormIsActive;
    },

    async getVehicles() {
      try {
        const res = await this.$axios.get("vehicles");
        this.vehicles = res.data.data.data.map((v) => ({
          ...v,
          name: v.plate_number || "-",
        }));
      } catch (e) {
        console.error(e.response?.data?.message);
      }
    },

    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/bookings/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions = {
        booking_number: "",
        provider_name: "",
        client_name: "",
        vehicle: null,
        status: null,
      };
      this.setTableRows();
    },

    updateRouterQueryParam(page) {
      this.$router.push({ query: { ...this.$route.query, page } });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async setTableRows() {
      this.loading = true;
      try {
        const res = await this.$axios.get("bookings", {
          params: {
            page: this.paginations.current_page,
            provider_name: this.filterOptions.provider_name || undefined,
            client_name: this.filterOptions.client_name || undefined,
            vehicle_id: this.filterOptions.vehicle?.id || undefined,
            booking_status: this.filterOptions.status?.value || undefined,
            booking_number: this.filterOptions.booking_number || undefined,
          },
        });

        const data = res.data.data;
        this.tableRows = data.data;
        this.paginations.last_page = data.meta.last_page;
        this.paginations.items_per_page = data.meta.per_page;
      } catch (error) {
        console.error(error.response?.data?.message);
      } finally {
        this.loading = false;
      }
    },

    getStatusText(status) {
      if (!status) return "-";
      const key = `STATUS.${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },

    // ✅ Status color logic
    getStatusColor(status) {
      const map = {
        external: "blue",
        not_paid: "grey",
        paid: "green",
        confirmed: "green",
        pending: "orange",
        in_progress: "teal",
        cancelled: "red",
        finished: "green",
        finished_penalty: "pink",
        deliver: "indigo",
        receive: "cyan",
      };
      return map[status] || "grey";
    },

    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios.delete(`bookings/${this.itemToDelete.id}`);
        this.dialogDelete = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response?.data?.message);
      }
    },

    showItem(item) {
      this.$router.push({ path: `/bookings/show/${item.id}` });
    },

    editItem(item) {
      this.$router.push({ path: `/bookings/edit/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => (this.filterFormIsActive = false));
    if (this.$route.query.page)
      this.paginations.current_page = +this.$route.query.page;
    this.getVehicles();
    this.setTableRows();
  },
};
</script>

<style scoped>
span.submit_btn,
span.reset_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s linear;
}

/* span.submit_btn {
  background-color: #f6a513;
}

span.reset_btn {
  background-color: #6c757d;
}  */
</style>
